// Forms/Import DSL — snippet library for the workbench editor. A curated, mostly
// exhaustive set of valid DSL building blocks the user inserts at the cursor and
// edits. Comments (# …) inside each snippet enumerate the allowed values for any
// multi-option field, so the editor doubles as living documentation.
//
// Quick reference (sigils + contexts):
//   :name      bind a value INTO SQL (query/call/exec/require/insert/update/delete)
//   {name}     interpolate a value INTO a display string (label/confirm/toast/…)
//   value cols are bare identifiers; `show`/`options` are lists.
//   user.id | user.name | user.role  → the current user (any expression).

export const SNIPPETS = [
  // ── Inputs ────────────────────────────────────────────────────────────────
  {
    group: 'Inputs',
    name: 'Field (text/number/date/check)',
    desc: 'Basic scalar inputs. type ∈ text | number | date | check.',
    code: `text   nama   { label = "Nama" }
number qty    { label = "Qty"; type = number; min = 0; max = 9999 }  # min/max: number only
date   tgl    { label = "Tanggal"; default = today() }               # default: literal | today() | now() | <ref> | sql.scalar("…")
check  lunas  { label = "Lunas" }
text   nomor  { label = "No"; default = sql.scalar("SELECT next_no()"); readonly = true }`,
  },
  {
    group: 'Inputs',
    name: 'Searchable picker (select)',
    desc: 'Dropdown from a query; value = the chosen row. search = true | [cols].',
    code: `select item {
  label  = "Item"
  query  = "SELECT id, name FROM my_table ORDER BY name"
  value  = id          # which column becomes the value of \`item\`
  display = name        # column shown in the dropdown (default: value/first col)
  search = true         # false (default) | true (all cols) | [name, code]
}`,
  },
  {
    group: 'Inputs',
    name: 'Static enum select',
    desc: 'Dropdown restricted to a fixed set (value/label objects, or bare strings).',
    code: `select status {
  label   = "Status"
  options = [{ value = "A", label = "Aktif" }, { value = "N", label = "Nonaktif" }]  # or just ["pcs", "box"]
  default = "A"
}`,
  },
  {
    group: 'Inputs',
    name: 'Connection + database pickers',
    desc: 'Let the user choose the server + database the form runs against.',
    code: `connection conn { label = "Server" }
database   db   { label = "Database" }   # lists databases on the chosen connection`,
  },
  {
    group: 'Inputs',
    name: 'Branch picker (preset conn+db)',
    desc: 'One alias dropdown that routes to a preset connection + database pair.',
    code: `target cabang {
  label = "Cabang"
  options = [
    { label = "Cabang Mojokerto", connection = "001 Bayiku Surodinawan", database = "dbBayiku" },
    { label = "Cabang Madiun",    connection = "002 Bayiku Madiun",      database = "dbBayiku" }
  ]   # keys: label, connection (name or id), database
}`,
  },
  {
    group: 'Inputs',
    name: 'Layout: row + section',
    desc: 'Group fields side-by-side (row) or under a titled box (section).',
    code: `section "Pelanggan" {
  row {
    select cust { label = "Customer"; query = "SELECT KodeCust, NamaCust FROM ms_cust"; value = KodeCust; search = true }
    date   tgl  { label = "Tanggal"; default = today() }
  }
  text alamat { label = "Alamat"; default = cust.Alamat; visible = cust is set }  # visible: reactive boolean
}`,
  },
  {
    group: 'Inputs',
    name: 'Layout: spacer (gap)',
    desc: 'Add extra vertical or horizontal breathing room between fields.',
    code: `text nama { label = "Nama" }
spacer { size = "1.5rem" }              # vertical gap — direction defaults to vertical
text alamat { label = "Alamat" }

row {
  text kota { label = "Kota" }
  spacer { direction = "horizontal"; size = "2rem" }   # fixed-width gap inside a row
  text kodepos { label = "Kode Pos" }
}`,
  },
  {
    group: 'Inputs',
    name: 'Formatted label / note',
    desc: 'bold / italic toggles + a size keyword (sm|md|lg|xl, or a raw CSS size).',
    code: `label judul { label = "Data Pelanggan"; bold = true; size = "lg" }   # section-style heading
note  hint  { label = "Semua field wajib diisi."; italic = true; size = "sm" }
label peringatan { label = "PERHATIAN"; bold = true; italic = true; size = "xl" }`,
  },

  // ── Lists ─────────────────────────────────────────────────────────────────
  {
    group: 'Lists',
    name: 'Search box + table',
    desc: 'A text box that filters a table across columns via the search() builtin.',
    code: `text q { label = "Cari" }
table results {
  label = "Hasil"
  query = "SELECT id, name, status FROM my_table WHERE search(:q, name, status) ORDER BY name"
  value = id
  show  = [name, status]
}`,
  },
  {
    group: 'Lists',
    name: 'Table with column alignment + format',
    desc: 'An optional column {} overlay on a table widget — align/format, same as a grid column.',
    code: `table results {
  label = "Penjualan"
  query = "SELECT NoNota, TglJual, Qty, Total FROM tr_jual ORDER BY TglJual DESC"
  value = NoNota
  show  = [NoNota, TglJual, Qty, Total]     # show still picks/orders the columns
  column Qty   { align = center }
  column Total { align = right; format = "#,##0" }   # align: left|right|center · format: number/date mask
}`,
  },
  {
    group: 'Lists',
    name: 'Table with built-in search',
    desc: 'search = [cols] renders its own box; { } form sets mode/min.',
    code: `table rows {
  label  = "Data"
  query  = "SELECT id, nama, kode FROM my_table ORDER BY nama"
  value  = id
  show   = [nama, kode]
  search = { columns = [nama, kode]; mode = "contains"; min = 2 }  # mode: contains | prefix | exact
}`,
  },
  {
    group: 'Lists',
    name: 'Searchable dropdown (multi-column)',
    desc: 'table + dropdown=true → a Select2-style collapsed picker with a multi-column, searchable popover.',
    code: `# Collapsed control shows the selected value; click → a search box + the rows as a
# multi-column table. dropdown=true on a table; pair with search=[cols] to filter.
table cust {
  label    = "Pelanggan"
  query    = "SELECT KodeCust, NamaCust, Kota FROM m_customer ORDER BY NamaCust"
  value    = KodeCust            # bound value (what :cust / {cust} resolve to)
  display  = NamaCust            # column shown in the collapsed control after picking
  show     = [KodeCust, NamaCust, Kota]   # columns in the dropdown table
  search   = [NamaCust, KodeCust]         # which columns the search box filters
  dropdown = true
}
label sel { label = "{cust}  —  pick a customer above" }`,
  },
  {
    group: 'Lists',
    name: 'Cascading select → table',
    desc: 'Pick a master; a detail table re-queries for it via :master.',
    code: `select master {
  label = "Master"
  query = "SELECT id, name FROM master_table ORDER BY name"
  value = id
}
table detail {
  label = "Detail"
  query = "SELECT id, line, qty FROM detail_table WHERE master_id = :master ORDER BY line"
  value = id
  show  = [line, qty]
}`,
  },

  // ── Grids ─────────────────────────────────────────────────────────────────
  {
    group: 'Grids',
    name: 'Editable master-detail grid',
    desc: 'Editable detail grid that saves added/edited/removed rows via SPs.',
    code: `select header {
  label = "Header"
  query = "SELECT id, code FROM header_table ORDER BY code"
  value = id
}
grid items {
  label  = "Items"
  query  = "SELECT id, code, qty FROM detail_table WHERE header_id = :header ORDER BY id"
  key    = id          # row identity (required for edit/delete tracking)
  add    = true        # allow inserting new rows
  remove = true        # allow deleting rows
  save   = "Simpan"    # Save button caption (default "💾 Save"); auto-disabled until there are changes
  insert = "sp_item_insert(:header, :row.code, :row.qty)"   # :row.<col> = the row's cell
  update = "sp_item_update(:row.id, :row.qty)"
  delete = "sp_item_delete(:row.id)"
  column code { edit = insert }            # edit: always (default) | insert | never
  column qty  { type = number; min = 0; required = true; align = right }  # align: left | right | center
}`,
  },
  {
    group: 'Grids',
    name: 'Computed column + total',
    desc: 'A computed (read-only) column and a footer aggregate.',
    code: `grid lines {
  label = "Lines"
  query = "SELECT id, qty, price FROM lines WHERE doc_id = :doc ORDER BY id"
  key   = id
  column qty   { type = number; align = right }
  column price { type = number; align = right }
  column total { computed = qty * price; format = "#,##0"; footer = sum; align = right; readonly = true }  # footer = sum · align = right (money)
}
let grand = sum(lines.rows.total)   # aggregates: sum | count | avg | min | max`,
  },
  {
    group: 'Grids',
    name: 'Grid with data hooks',
    desc: 'Before/after insert|update|delete hooks, run around each row on save.',
    code: `grid items {
  label  = "Items"
  query  = "SELECT id, qty FROM detail_table WHERE doc_id = :doc ORDER BY id"
  key    = id
  insert = "sp_item_insert(:doc, :row.qty)"
  delete = "sp_item_delete(:row.id)"
  before delete { require "SELECT 1 FROM doc WHERE id = :doc AND posted = 0" else "Sudah diposting." }
  after  insert { exec "CALL sp_recalc(:doc)" }
  after  delete { exec "CALL sp_recalc(:doc)" }
}`,
  },

  {
    group: 'Grids',
    name: 'Enum / dropdown column',
    desc: 'Constrain a grid column to a fixed set of values (type = select + options).',
    code: `grid items {
  label  = "Item"
  query  = "SELECT id, kode, satuan, status FROM faktur_item WHERE faktur_id = :header ORDER BY id"
  key    = id
  add    = true
  insert = "sp_item_insert(:header, :row.kode, :row.satuan, :row.status)"
  update = "sp_item_update(:row.id, :row.satuan, :row.status)"
  column kode   { edit = insert }                          # set on insert, locked after
  column status {                                          # stored = A/N, shown = Aktif/Nonaktif
    type    = select
    options = [{ value = "A", label = "Aktif" }, { value = "N", label = "Nonaktif" }]
    required = true
  }
  column satuan { type = select; options = ["pcs", "box", "lusin"] }   # bare strings: value = label
}`,
  },

  {
    group: 'Grids',
    name: 'Lookup (FK) column',
    desc: 'A grid column whose dropdown options come from a table (value, label).',
    code: `grid items {
  label  = "Item"
  query  = "SELECT id, kode, qty FROM faktur_item WHERE faktur_id = :header ORDER BY id"
  key    = id
  add    = true
  insert = "sp_item_insert(:header, :row.kode, :row.qty)"
  column kode {
    lookup = "SELECT KodeBrg, NamaBrg FROM m_barang ORDER BY NamaBrg"  # value = 1st col, label = 2nd
    # map = NamaBrg   # optional: which column to show as the label
  }
  column qty { type = number; min = 0; required = true }
}`,
  },
  {
    group: 'Flow',
    name: 'Loop over rows (for)',
    desc: 'Run steps once per item of an array — a sql.rows let or a grid accessor.',
    code: `# Iterate any array in scope: a \`let\` of sql.rows, or a grid's g.rows / g.added /
# g.edited / g.removed. Inside, :row.<col> binds the current item's cell.
let pending = sql.rows("SELECT id, qty FROM antrian WHERE status = 'baru'")
button "Proses semua" {
  run {
    for row in pending {
      call "sp_proses(:row.id, :row.qty)"
    }
    toast "Selesai memproses."
    close()
  }
}`,
  },

  // ── Actions ───────────────────────────────────────────────────────────────
  {
    group: 'Actions',
    name: 'Delete button (confirm)',
    desc: 'A confirm button that calls a delete SP and refreshes.',
    code: `button "Hapus" {
  enabled = item is set            # is set | is empty | comparisons
  confirm = "Hapus {item}?"        # blocking yes/cancel before run
  run {
    call "sp_delete(:item)"
    toast "Terhapus."
    refresh item                    # re-run that widget's query
  }
}`,
  },
  {
    group: 'Actions',
    name: 'Save button',
    desc: 'Calls an SP with field values, then closes the form.',
    code: `button "Simpan" {
  enabled = nama != ""
  run {
    call "sp_save(:nama, :qty, :tgl)"
    toast "Tersimpan."
    close()
  }
}`,
  },
  {
    group: 'Actions',
    name: 'Call SP with session vars (with)',
    desc: 'Presets @v<Name> then CALLs a parameterless proc — the classic SP pattern.',
    code: `# \`with { Name = expr }\` SETs @v<Name> before the call. Use it for stored
# procedures that read free-standing @v… session vars and take no arguments.
# (e.g. \`with { KodeBrg = kodeBrg }\` → SET @vKodeBrg, then CALL sp_insertbarang())
# @vNinocatUser (the logged-in user) is set automatically — no need to list it.
button "Simpan" {
  enabled = kodeBrg != ""
  run {
    call "sp_insertbarang()" with {
      KodeBrg = kodeBrg,   # → @vKodeBrg
      NamaBrg = namaBrg,   # → @vNamaBrg
    }
    toast "Tersimpan."
    close()
  }
}`,
  },
  {
    group: 'Actions',
    name: 'Messages & dialogs',
    desc: 'toast (ok) · info · warn · error (toasts) · alert (must-acknowledge).',
    code: `button "Cek" {
  run {
    toast "Berhasil."        # success toast
    info  "Sekadar info."    # info toast
    warn  "Perlu perhatian." # warning toast
    error "Ada masalah."     # error toast
    alert "Harus diklik OK." # blocking dialog
  }
}`,
  },
  {
    group: 'Actions',
    name: 'Open another form',
    desc: 'Navigate to another form, passing parameters.',
    code: `button "Cetak" {
  enabled = noJual is set
  run { open "CetakFaktur" with { noJual = noJual } }   # the target form declares: param noJual { … }
}`,
  },

  // ── Flow ──────────────────────────────────────────────────────────────────
  {
    group: 'Flow',
    name: 'Branching (if / when)',
    desc: 'Conditional steps in a run block. if/else two-way; when = one-way.',
    code: `button "Proses" {
  run {
    require qty > 0 else "Qty harus lebih dari 0."
    if status == "baru" {
      call "sp_proses_baru(:id)"
    } else {
      call "sp_proses_lama(:id)"
    }
    when qty > 100 { warn "Qty besar — mohon diperiksa." }   # when: runs the block only if true
    toast "Selesai."
  }
}`,
  },
  {
    group: 'Flow',
    name: 'Conditional visibility',
    desc: 'Show/hide a field reactively (excluded from binds when hidden).',
    code: `select jenis { label = "Jenis"; options = ["Tunai", "Kredit"]; default = "Tunai" }
number tenor { label = "Tenor (bulan)"; type = number; visible = jenis == "Kredit" }`,
  },
  {
    group: 'Flow',
    name: 'Derived value (let) — sync & async query',
    desc: 'let derives a value: sync (from bindings) or async sql.scalar/row/rows. Usable in labels, enabled, with{}, :binds.',
    code: `# \`let\` derives a value. SYNC: from other bindings (results.Col, arithmetic,
# concat). ASYNC: sql.scalar/row/rows("…") runs a query reactively (re-running
# when its :binds change). Both work in {interp}, expressions, enabled, with{}.
text q { label = "No Jual" }

# async existence check — re-runs as you type:
let ada  = sql.scalar("SELECT COUNT(*) FROM tr_jualr_m WHERE NoJualR = :q")
let info = if(ada > 0, concat("Nota ", q, " ADA"), concat("Nota ", q, " tidak ada"))
label cek { label = "{info}" }   # {name} interpolates a scalar / value-column / let

button "Hapus" {
  enabled = ada > 0
  run {
    call "sp_hapus()" with { NoNota = q }   # call auto-prefixes CALL
    toast "Hapus {q} berhasil."
    close()
  }
}`,
  },

  // ── Rules ─────────────────────────────────────────────────────────────────
  {
    group: 'Rules',
    name: 'Validation',
    desc: 'Cross-field rules before a mutating action. require <expr | "SQL"> else "…".',
    code: `validate {
  require qty > 0                                  else "Qty harus lebih dari 0."
  require tglAkhir >= tglAwal                      else "Tanggal akhir < awal."
  require "SELECT 1 FROM ms_cust WHERE Kode = :cust" else "Customer tidak valid."
}`,
  },

  // ── Import ────────────────────────────────────────────────────────────────
  {
    group: 'Import',
    name: 'Import (per-row)',
    desc: 'Map a file’s columns and run an SP per row.',
    code: `import {
  source = file { format = "csv"; header = true; delimiter = auto }  # format: csv | xlsx (+ sheet="…")
  column code { from = "Code"; required = true }                     # from = the file header
  column tgl  { from = "Tanggal"; type = date; format = "dd/MM/yyyy" }
  column qty  { from = "Qty"; type = number; min = 0 }
  onError     = ask          # skip (report) | ask (pause) | abort
  transaction = perRow       # perRow | all
  each row {
    require "SELECT 1 FROM ms_item WHERE code = :code" else "Item tidak dikenal"
    call "sp_import(:code, :tgl, :qty)"
  }
}`,
  },

  // ── Reference ─────────────────────────────────────────────────────────────
  {
    group: 'Reference',
    name: 'meta (form header)',
    desc: 'Optional form-level config.',
    code: `meta {
  title      = "Judul Form"
  width      = "md"     # sm | md | lg
  connection = "Production"   # pin a connection (omit to use a picker)
  database   = "sales"        # pin a database
  locale     = "id-ID"        # number/date formatting locale
}`,
  },
  {
    group: 'Reference',
    name: 'param (opening context)',
    desc: 'Values the form is opened with (e.g. from another form’s open …).',
    code: `param noJual { type = text; required = true }   # type: text|number|date · required · default
param mode   { type = text; default = "edit" }`,
  },
];
